import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Activity, Eye, Users, MousePointerClick, TrendingUp, Globe, Monitor, Smartphone, Tablet,
  Search as SearchIcon, Compass, ArrowUpRight, FileText,
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar,
} from 'recharts';
import { api } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';
import { logger } from '@/lib/logger';
import EmptyState from '@/components/admin/EmptyState';
import { countryInfo, pageLabel } from '@/constants/countries';

const RANGES = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: '7 Days' },
  { id: '30d', label: '30 Days' },
  { id: '90d', label: '90 Days' },
  { id: 'all', label: 'All Time' },
];

const num = (v) => Number(v || 0).toLocaleString();
const pct = (v) => `${Number(v || 0).toFixed(1)}%`;

const deviceIcon = (key) => {
  switch (key) {
    case 'mobile': return Smartphone;
    case 'tablet': return Tablet;
    case 'desktop': return Monitor;
    default: return Compass;
  }
};

const cssVar = (name, fallback) => {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
};

// Shared chart tokens (consistent across all charts)
const AXIS_STYLE = { stroke: 'rgba(255,255,255,0.35)', fontSize: 11 };
const GRID_STROKE = 'rgba(255,255,255,0.06)';
const TOOLTIP_STYLE = {
  background: 'rgba(15,15,17,0.96)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 12,
  color: '#fff',
  fontSize: 12,
  padding: '8px 12px',
  boxShadow: '0 12px 30px rgba(0,0,0,0.5)',
};

// Softer palette that harmonizes with the active accent
const chartColor = (i, accent) => {
  const palette = [accent, '#22c55e', '#3B82F6', '#a855f7', '#f97316', '#f43f5e', '#14b8a6', '#eab308'];
  return palette[i % palette.length];
};

export default function AdminAnalytics() {
  const [range, setRange] = useState('7d');
  const [summary, setSummary] = useState(null);
  const [timeseries, setTimeseries] = useState({ granularity: 'day', series: [] });
  const [topPages, setTopPages] = useState([]);
  const [sources, setSources] = useState([]);
  const [devices, setDevices] = useState([]);
  const [countries, setCountries] = useState([]);
  const [funnels, setFunnels] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (rng) => {
    setLoading(true);
    try {
      const [s, ts, pages, src, dev, ctry, fn] = await Promise.all([
        api.get(`/admin/analytics/summary?range=${rng}`),
        api.get(`/admin/analytics/timeseries?range=${rng}`),
        api.get(`/admin/analytics/top-pages?range=${rng}`),
        api.get(`/admin/analytics/sources?range=${rng}`),
        api.get(`/admin/analytics/devices?range=${rng}`),
        api.get(`/admin/analytics/countries?range=${rng}`),
        api.get(`/admin/analytics/funnels?range=${rng}`),
      ]);
      setSummary(s.data);
      setTimeseries(ts.data || { series: [] });
      setTopPages(pages.data?.items || []);
      setSources(src.data?.items || []);
      setDevices(dev.data?.items || []);
      setCountries(ctry.data?.items || []);
      setFunnels(fn.data || null);
    } catch (err) {
      logger.warn('Failed to load analytics', err);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(range); }, [range, load]);

  const accent = cssVar('--pg-admin-accent', '#F2B705');
  const chartXFormatter = useCallback((v) => {
    if (!v) return '';
    if (timeseries.granularity === 'hour') return v.slice(11, 13) + 'h';
    try {
      const d = new Date(v);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } catch { return v; }
  }, [timeseries.granularity]);

  const timeseriesTotal = useMemo(() => (timeseries.series || []).reduce((s, p) => s + (p.views || 0), 0), [timeseries.series]);
  const useBarsForTimeseries = (timeseries.series || []).length <= 2;

  const totalSourceViews = useMemo(() => sources.reduce((s, x) => s + (x.views || 0), 0), [sources]);
  const totalDeviceViews = useMemo(() => devices.reduce((s, x) => s + (x.views || 0), 0), [devices]);
  const totalCountryViews = useMemo(() => countries.reduce((s, x) => s + (x.views || 0), 0), [countries]);

  const noData = !loading && (summary?.total_views ?? 0) === 0;

  return (
    <div data-testid={ADMIN.analyticsPage}>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl leading-tight tracking-tight inline-flex items-center gap-2">
            <Activity className="w-7 h-7 text-[var(--pg-admin-accent)]" /> Analytics
          </h1>
          <p className="mt-1 text-sm text-white/60">Anonymous visitor data. No PII stored — just what you need to grow.</p>
        </div>
        <div data-testid={ADMIN.analyticsRangeSelect} className="inline-flex rounded-xl border border-white/10 bg-[#141414] p-1 overflow-x-auto">
          {RANGES.map((r) => (
            <button
              key={r.id}
              data-testid={ADMIN.analyticsRangeOption(r.id)}
              onClick={() => setRange(r.id)}
              className={`px-3 py-1.5 text-xs rounded-lg whitespace-nowrap transition-colors ${range === r.id ? 'bg-[var(--pg-admin-accent)] text-[var(--pg-admin-accent-fg)] font-semibold' : 'text-white/70 hover:text-white'}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
        <Kpi testid={ADMIN.analyticsKpi('views')} icon={Eye} label="Page views" value={num(summary?.total_views)} sub={`${num(summary?.sessions)} sessions`} tone="accent" loading={loading} />
        <Kpi testid={ADMIN.analyticsKpi('visitors')} icon={Users} label="Unique visitors" value={num(summary?.unique_visitors)} sub={`${summary?.views_per_session ?? 0} views/session`} loading={loading} />
        <Kpi testid={ADMIN.analyticsKpi('booking-conv')} icon={TrendingUp} label="Booking conv." value={pct(summary?.booking?.conversion)} sub={`${num(summary?.booking?.submits)} of ${num(summary?.booking?.starts)}`} tone="ok" loading={loading} />
        <Kpi testid={ADMIN.analyticsKpi('contact-conv')} icon={MousePointerClick} label="Contact conv." value={pct(summary?.contact?.conversion)} sub={`${num(summary?.contact?.submits)} of ${num(summary?.contact?.starts)}`} loading={loading} />
      </div>

      {noData ? (
        <Panel className="mt-6">
          <EmptyState
            title="Waiting for your first visitor"
            description="Analytics start flowing the moment someone lands on your public site. Share your link on Instagram or with a friend to see this dashboard come alive."
            icon={Activity}
          />
        </Panel>
      ) : (
        <>
          {/* Traffic over time */}
          <Panel
            title="Traffic over time"
            badge={`${num(timeseriesTotal)} views`}
            className="mt-6"
            testid={ADMIN.analyticsChart('timeseries')}
          >
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                {useBarsForTimeseries ? (
                  <BarChart data={timeseries.series} margin={{ top: 12, right: 8, left: -12, bottom: 4 }} barCategoryGap="30%">
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
                    <XAxis dataKey="key" tickFormatter={chartXFormatter} {...AXIS_STYLE} tickLine={false} axisLine={false} />
                    <YAxis {...AXIS_STYLE} allowDecimals={false} tickLine={false} axisLine={false} width={32} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} labelFormatter={chartXFormatter} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                    <Bar dataKey="views" name="Views" fill={accent} radius={[8, 8, 0, 0]} maxBarSize={72} />
                    <Bar dataKey="uniques" name="Unique" fill="rgba(255,255,255,0.35)" radius={[8, 8, 0, 0]} maxBarSize={72} />
                  </BarChart>
                ) : (
                  <AreaChart data={timeseries.series} margin={{ top: 12, right: 8, left: -12, bottom: 4 }}>
                    <defs>
                      <linearGradient id="fillViews" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={accent} stopOpacity={0.55} />
                        <stop offset="100%" stopColor={accent} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="fillUniques" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ffffff" stopOpacity={0.28} />
                        <stop offset="100%" stopColor="#ffffff" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
                    <XAxis dataKey="key" tickFormatter={chartXFormatter} {...AXIS_STYLE} tickLine={false} axisLine={false} minTickGap={24} />
                    <YAxis {...AXIS_STYLE} allowDecimals={false} tickLine={false} axisLine={false} width={32} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} labelFormatter={chartXFormatter} cursor={{ stroke: accent, strokeOpacity: 0.4, strokeDasharray: '3 3' }} />
                    <Area type="monotone" dataKey="views" stroke={accent} strokeWidth={2.5} fill="url(#fillViews)" name="Views" activeDot={{ r: 5, stroke: '#0B0C0F', strokeWidth: 2 }} />
                    <Area type="monotone" dataKey="uniques" stroke="rgba(255,255,255,0.7)" strokeWidth={1.5} strokeDasharray="4 3" fill="url(#fillUniques)" name="Unique" />
                  </AreaChart>
                )}
              </ResponsiveContainer>
            </div>
          </Panel>

          {/* Funnels */}
          <div className="mt-4 grid lg:grid-cols-12 gap-4">
            <Panel title="Booking funnel" badge={`${pct(funnels?.booking?.conversion)}`} className="lg:col-span-6" testid={ADMIN.analyticsChart('funnel-booking')}>
              <FunnelBars data={funnels?.booking} accent={accent} />
            </Panel>
            <Panel title="Contact funnel" badge={`${pct(funnels?.contact?.conversion)}`} className="lg:col-span-6" testid={ADMIN.analyticsChart('funnel-contact')}>
              <FunnelBars data={funnels?.contact} accent={accent} />
            </Panel>
          </div>

          {/* Sources / Devices / Countries */}
          <div className="mt-4 grid lg:grid-cols-12 gap-4">
            <Panel title="Traffic sources" badge={`${num(totalSourceViews)} views`} className="lg:col-span-5" testid={ADMIN.analyticsChart('sources')}>
              {sources.length === 0 ? <EmptyState title="No source data" icon={SearchIcon} /> : (
                <div className="grid grid-cols-[130px_1fr] items-center gap-3 sm:gap-5">
                  <div className="relative w-[130px] h-[130px] mx-auto">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={sources} dataKey="views" nameKey="key" cx="50%" cy="50%" innerRadius={44} outerRadius={62} paddingAngle={3} stroke="none">
                          {sources.map((s, i) => (
                            <Cell key={s.key} fill={chartColor(i, accent)} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={TOOLTIP_STYLE} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <div className="font-display text-lg leading-none">{num(totalSourceViews)}</div>
                      <div className="text-[9px] uppercase tracking-widest text-white/50 mt-0.5">Total</div>
                    </div>
                  </div>
                  <ul className="grid grid-cols-1 gap-1.5">
                    {sources.slice(0, 6).map((s, i) => (
                      <li key={s.key} className="flex items-center gap-2 text-sm min-w-0">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: chartColor(i, accent) }} />
                        <span className="truncate flex-1 text-white/85">{s.key || 'unknown'}</span>
                        <span className="text-white/60 tabular-nums shrink-0">{num(s.views)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Panel>

            <Panel title="Devices" badge={`${num(totalDeviceViews)} views`} className="lg:col-span-3" testid={ADMIN.analyticsChart('devices')}>
              {devices.length === 0 ? <EmptyState title="No devices yet" icon={Monitor} /> : (
                <ul className="space-y-3">
                  {devices.map((d) => {
                    const Icon = deviceIcon(d.key);
                    const max = Math.max(...devices.map((x) => x.views), 1);
                    const w = (d.views / max) * 100;
                    const share = totalDeviceViews ? Math.round((d.views / totalDeviceViews) * 100) : 0;
                    return (
                      <li key={d.key}>
                        <div className="flex items-center justify-between text-sm mb-1.5">
                          <span className="inline-flex items-center gap-2 capitalize text-white/85"><Icon className="w-3.5 h-3.5 text-white/60" /> {d.key}</span>
                          <span className="text-white/60 tabular-nums text-xs">{num(d.views)} <span className="text-white/40">· {share}%</span></span>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                          <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${Math.max(3, w)}%`, background: `linear-gradient(90deg, ${accent}, ${accent}CC)` }} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Panel>

            <Panel title="Countries" badge={`${num(totalCountryViews)} views`} className="lg:col-span-4" testid={ADMIN.analyticsChart('countries')}>
              {countries.length === 0 ? <EmptyState title="No country data" icon={Globe} /> : (
                <ul className="space-y-2 max-h-[360px] overflow-y-auto pr-1 -mr-1">
                  {countries.slice(0, 10).map((c, i) => {
                    const info = countryInfo(c.key);
                    const share = totalCountryViews ? (c.views / totalCountryViews) * 100 : 0;
                    const max = Math.max(...countries.map((x) => x.views), 1);
                    const w = (c.views / max) * 100;
                    const isUnknown = info.code === '??';
                    return (
                      <li key={c.key} className="rounded-lg hover:bg-white/5 transition-colors py-1.5 pl-1.5 pr-2">
                        <div className="flex items-center gap-2.5 mb-1">
                          <span className="w-5 text-[10px] tabular-nums text-white/40 font-semibold text-center">{i + 1}</span>
                          <span className={`text-lg leading-none ${isUnknown ? 'grayscale opacity-60' : ''}`} aria-hidden>{info.flag}</span>
                          <span className="flex-1 min-w-0 flex items-baseline gap-2 min-h-[18px]">
                            <span className="text-sm text-white/90 truncate">{info.name}</span>
                            {!isUnknown && <span className="text-[10px] text-white/40 tracking-wider">{info.code}</span>}
                          </span>
                          <span className="text-white/70 text-xs tabular-nums shrink-0">{num(c.views)}</span>
                          <span className="text-white/40 text-[10px] tabular-nums shrink-0 w-10 text-right">{share.toFixed(1)}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/5 overflow-hidden ml-9">
                          <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${Math.max(3, w)}%`, background: isUnknown ? 'rgba(255,255,255,0.25)' : `linear-gradient(90deg, ${accent}, ${accent}AA)` }} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Panel>
          </div>

          {/* Top pages */}
          <Panel title="Top pages" badge={`${topPages.length} page${topPages.length === 1 ? '' : 's'}`} className="mt-4" testid={ADMIN.analyticsChart('top-pages')}>
            {topPages.length === 0 ? <EmptyState title="No page data yet" icon={Compass} /> : (
              <ul className="divide-y divide-white/5">
                {topPages.map((p, i) => {
                  const max = Math.max(...topPages.map((x) => x.views), 1);
                  const w = (p.views / max) * 100;
                  const share = summary?.total_views ? (p.views / summary.total_views) * 100 : 0;
                  const label = pageLabel(p.path);
                  const rawShown = label !== p.path;
                  return (
                    <li key={p.path} className="py-3 first:pt-0 last:pb-0">
                      <div className="flex items-center gap-3">
                        <span className="w-6 text-[10px] tabular-nums text-white/40 font-semibold text-center">{i + 1}</span>
                        <span className="inline-flex w-8 h-8 shrink-0 items-center justify-center rounded-lg bg-white/5 text-white/60"><FileText className="w-3.5 h-3.5" /></span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-white/90 truncate">{label}</div>
                          {rawShown && <div className="text-[11px] text-white/40 truncate">{p.path}</div>}
                        </div>
                        <div className="hidden sm:block text-right shrink-0 min-w-[70px]">
                          <div className="text-[10px] text-white/40 uppercase tracking-widest">Uniques</div>
                          <div className="text-sm tabular-nums text-white/80">{num(p.uniques)}</div>
                        </div>
                        <div className="text-right shrink-0 min-w-[70px]">
                          <div className="text-[10px] text-white/40 uppercase tracking-widest">Views</div>
                          <div className="text-sm tabular-nums text-white font-semibold">{num(p.views)}</div>
                        </div>
                        <div className="hidden md:block text-right shrink-0 w-14">
                          <div className="text-[10px] text-white/40 uppercase tracking-widest">Share</div>
                          <div className="text-sm tabular-nums text-white/70">{share.toFixed(1)}%</div>
                        </div>
                      </div>
                      <div className="mt-2 ml-9 h-1.5 rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${Math.max(3, w)}%`, background: `linear-gradient(90deg, ${accent}, ${accent}AA)` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}

const Panel = ({ title, badge, children, className = '', testid }) => (
  <section data-testid={testid} className={`rounded-2xl border border-white/10 bg-[#141414] overflow-hidden shadow-[0_12px_30px_rgba(0,0,0,0.35)] ${className}`}>
    {title && (
      <header className="px-5 py-3 border-b border-white/5 flex items-center justify-between gap-3">
        <div className="text-[11px] uppercase tracking-widest text-white/60 font-semibold inline-flex items-center gap-2">
          <span className="w-1 h-3.5 rounded-full bg-[var(--pg-admin-accent)]" />
          {title}
        </div>
        {badge && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--pg-admin-accent-soft)] text-[var(--pg-admin-accent)] text-[10px] font-semibold uppercase tracking-widest">
            <ArrowUpRight className="w-3 h-3" /> {badge}
          </span>
        )}
      </header>
    )}
    <div className="p-4 sm:p-5">{children}</div>
  </section>
);

const Kpi = ({ icon: Icon, label, value, sub, tone = 'default', loading = false, testid }) => {
  const toneClass = {
    default: 'bg-white/5 text-white',
    accent: 'bg-[var(--pg-admin-accent)] text-[var(--pg-admin-accent-fg)]',
    ok: 'bg-green-500/15 text-green-300',
  }[tone];
  return (
    <div data-testid={testid} className="rounded-2xl border border-white/10 bg-[#141414] p-3 sm:p-4 flex items-center gap-2.5 sm:gap-3 shadow-[0_12px_30px_rgba(0,0,0,0.35)]">
      <div className={`inline-flex w-9 h-9 sm:w-11 sm:h-11 items-center justify-center rounded-xl shrink-0 ${toneClass}`}>
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
      </div>
      <div className="min-w-0">
        <div className="font-display text-base sm:text-xl leading-none truncate">{loading ? '—' : value}</div>
        <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1 truncate">{label}</div>
        {sub && <div className="text-[10px] text-white/40 mt-0.5 truncate hidden sm:block">{sub}</div>}
      </div>
    </div>
  );
};

const FunnelBars = ({ data, accent }) => {
  if (!data) return <div className="text-sm text-white/50">No data.</div>;
  const steps = [
    { key: 'views', label: 'Site views', value: data.views },
    { key: 'starts', label: 'Started form', value: data.starts },
    { key: 'submits', label: 'Submitted', value: data.submits },
  ];
  const max = Math.max(...steps.map((s) => s.value), 1);
  return (
    <div className="space-y-3.5">
      {steps.map((s, i) => {
        const w = (s.value / max) * 100;
        const isFinal = i === 2;
        const barColor = isFinal ? '#22c55e' : accent;
        const share = max ? Math.round((s.value / max) * 100) : 0;
        return (
          <div key={s.key}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="inline-flex items-center gap-2 text-white/85">
                <span className="inline-flex w-5 h-5 items-center justify-center rounded-full bg-white/5 text-[10px] font-semibold text-white/60">{i + 1}</span>
                {s.label}
              </span>
              <span className="text-white/70 tabular-nums text-xs">
                {Number(s.value || 0).toLocaleString()}
                <span className="text-white/40"> · {share}%</span>
              </span>
            </div>
            <div className="h-2.5 rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full rounded-full transition-[width] duration-500"
                style={{
                  width: `${Math.max(3, w)}%`,
                  background: `linear-gradient(90deg, ${barColor}, ${barColor}CC)`,
                  opacity: i === 0 ? 0.75 : i === 1 ? 0.9 : 1,
                }}
              />
            </div>
          </div>
        );
      })}
      <div className="pt-1.5 border-t border-white/5 flex items-center justify-between text-xs">
        <span className="text-white/50 uppercase tracking-widest">Overall conversion</span>
        <span className="text-white font-semibold">{Number(data.conversion || 0).toFixed(1)}%</span>
      </div>
    </div>
  );
};
